import type { EPaymentMethodEnum } from '@/shared/api/generated';
import {
  useGetApiV1IndividualpaymentCourseFinancialsCoursefinancialidAvailableNumberParcels,
  useGetApiV1IndividualpaymentEditorsDiscountTypes,
  useGetApiV1IndividualpaymentEditorsDiscountTypesDiscounttypeidPercentage,
  useGetApiV1IndividualpaymentEditorsDiscountTypesDiscounttypeidRetroactive,
  useGetApiV1IndividualpaymentEditorsScholarshipTypes,
  useGetApiV1IndividualpaymentEditorsScholarshipTypesScholarshiptypeidPercentage,
  useGetApiV1IndividualpaymentFinancialPeopleFinancialpersonidAvailablePaymentMethods,
} from '@/shared/api/generated';
import { type LookupOption, mapDropdownOptions } from '@/shared/ui/molecules/LookupAutocomplete';

/**
 * Tipos de bolsa disponíveis.
 *
 * @returns `{ scholarshipTypes, isLoading }`
 */
export function useScholarshipTypesDropdown(): {
  readonly scholarshipTypes: readonly LookupOption[];
  readonly isLoading: boolean;
} {
  const { data, isLoading } = useGetApiV1IndividualpaymentEditorsScholarshipTypes();
  return { scholarshipTypes: mapDropdownOptions(data), isLoading } as const;
}

/**
 * Percentual sugerido para um tipo de bolsa.
 *
 * É sugestão, não imposição: o operador pode sobrescrever para o plano.
 *
 * @param scholarshipTypeId - Tipo selecionado; `null` desabilita a query.
 * @returns `{ percentage, isLoading }`
 */
export function useScholarshipTypePercentage(scholarshipTypeId: number | null): {
  readonly percentage: number | null;
  readonly isLoading: boolean;
} {
  const { data, isLoading } =
    useGetApiV1IndividualpaymentEditorsScholarshipTypesScholarshiptypeidPercentage(
      scholarshipTypeId ?? 0,
      { query: { enabled: scholarshipTypeId !== null } },
    );

  return {
    percentage: typeof data === 'number' ? data : null,
    isLoading: scholarshipTypeId !== null && isLoading,
  } as const;
}

/**
 * Tipo de desconto disponível para inclusão.
 *
 * Não é um `LookupOption` comum: `mustSelectAssociatedCompany` decide se a
 * empresa conveniada passa a ser obrigatória no modal. Achatar para
 * `{ key, value }` perderia justamente a informação que muda a validação.
 */
export interface DiscountTypeOption {
  readonly id: number;
  readonly label: string;
  readonly mustSelectAssociatedCompany: boolean;
}

/**
 * Tipos de desconto disponíveis para inclusão na tabela de descontos.
 *
 * @returns `{ discountTypes, isLoading }`
 */
export function useDiscountTypesDropdown(): {
  readonly discountTypes: readonly DiscountTypeOption[];
  readonly isLoading: boolean;
} {
  const { data, isLoading } = useGetApiV1IndividualpaymentEditorsDiscountTypes();

  const discountTypes = (data ?? [])
    .filter(item => item.id !== undefined)
    .map(item => ({
      id: item.id as number,
      label: item.description ?? '',
      mustSelectAssociatedCompany: item.mustSelectAssociatedCompany ?? false,
    }));

  return { discountTypes, isLoading } as const;
}

/**
 * Percentual e retroatividade sugeridos de um tipo de desconto.
 *
 * @param discountTypeId - Tipo selecionado; `null` desabilita as queries.
 * @returns `{ percentage, retroactive, isLoading }`
 */
export function useDiscountTypeDefaults(discountTypeId: number | null): {
  readonly percentage: number | null;
  readonly retroactive: boolean | null;
  readonly isLoading: boolean;
} {
  const enabled = discountTypeId !== null;

  const percentageQuery = useGetApiV1IndividualpaymentEditorsDiscountTypesDiscounttypeidPercentage(
    discountTypeId ?? 0,
    { query: { enabled } },
  );

  const retroactiveQuery =
    useGetApiV1IndividualpaymentEditorsDiscountTypesDiscounttypeidRetroactive(discountTypeId ?? 0, {
      query: { enabled },
    });

  return {
    percentage: typeof percentageQuery.data === 'number' ? percentageQuery.data : null,
    retroactive: typeof retroactiveQuery.data === 'boolean' ? retroactiveQuery.data : null,
    isLoading: enabled && (percentageQuery.isLoading || retroactiveQuery.isLoading),
  } as const;
}

/**
 * Formas de pagamento aceitas por este contrato.
 *
 * @param financialPersonId - ID do contrato; `null` desabilita a query.
 * @returns `{ paymentMethods, isLoading }`
 */
export function useAvailablePaymentMethods(financialPersonId: number | null): {
  readonly paymentMethods: readonly LookupOption[];
  readonly isLoading: boolean;
} {
  const { data, isLoading } =
    useGetApiV1IndividualpaymentFinancialPeopleFinancialpersonidAvailablePaymentMethods(
      financialPersonId ?? 0,
      undefined,
      { query: { enabled: financialPersonId !== null } },
    );

  return {
    paymentMethods: mapDropdownOptions(data),
    isLoading: financialPersonId !== null && isLoading,
  } as const;
}

/**
 * Números de parcela aceitos para o plano financeiro do curso.
 *
 * As opções **dependem da forma de pagamento** — o endpoint é descrito como
 * "opções de número de parcelas do método de pagamento". Sem `paymentMethod` o
 * backend não tem como restringir a lista, então a query só roda quando ele
 * existe.
 *
 * A resposta é `DropDownListViewModel[]`, não uma lista de inteiros: o `key`
 * carrega a quantidade e o `value`, o rótulo exibido.
 *
 * @param courseFinancialId - Plano financeiro do curso; `null` desabilita a query.
 * @param paymentMethod - Forma de pagamento escolhida; `null` desabilita a query.
 * @param currentNumberParcels - Quantidade atual, para o backend mantê-la na lista.
 * @returns `{ options, isLoading }`
 */
export function useAvailableNumberParcels(
  courseFinancialId: number | null,
  paymentMethod: number | null,
  currentNumberParcels?: number | undefined,
): {
  readonly options: readonly LookupOption[];
  readonly isLoading: boolean;
} {
  const enabled = courseFinancialId !== null && paymentMethod !== null;

  const { data, isLoading } =
    useGetApiV1IndividualpaymentCourseFinancialsCoursefinancialidAvailableNumberParcels(
      courseFinancialId ?? 0,
      enabled
        ? {
            paymentMethod: paymentMethod as EPaymentMethodEnum,
            ...(currentNumberParcels === undefined ? {} : { currentNumberParcels }),
          }
        : undefined,
      { query: { enabled } },
    );

  return { options: mapDropdownOptions(data), isLoading: enabled && isLoading } as const;
}
